import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
} from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { PinActivationService } from '../../../core/services/pin-activation.service';
import {
  IncidentType,
  Permission,
  PinActivationDialogData,
} from '../../../core/interfaces/pin-activation.types';
import { IncidentCategory } from '../../../core/models/incident-category.model';
import { Organization } from '../../../core/models/model';
import { Floor, Room } from '../../../core/models/organization.interface';

import { FormsModule } from '@angular/forms';
import { OrganizationUtilService } from '../../../core/services/organization-util.service';
import { DataService } from '../../../core/services/data.service';
import { GlobalSettingsService } from '../../../core/services/global-settings.service';
import _ from 'lodash'; // Make sure lodash is imported
import { SettingsService } from '../../../core/services/settings.service';
import { IncidentCategoryService } from '../../../core/services/incident-category.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ResponseModalComponent } from '../response-modal/response-modal.component'; // adjust path as needed
import { PinKeypadComponent } from '../common-modal/pin-keypad.component';
import { IllumLiveService } from '../../../core/services/illum-live.service';

@Component({
  selector: 'app-pin-activation-dialog',
  standalone: true,
  imports: [FormsModule, PinKeypadComponent],
  templateUrl: './pin-activation-dialog.component.html',
  styleUrl: './pin-activation-dialog.component.css',
})
export class PinActivationDialogComponent
  implements OnInit, OnDestroy, OnChanges
{
  // Dialog state
  isActivateIncident = false;
  isOperateIncident = false;
  incidentCreatedAndIsPending = false;
  orgIncidentCategoryDict: Record<string, IncidentCategory[]> = {};
  // Header and messages
  headerMsg = '';
  bottomMsg = '';

  // PIN input
  pinValue: string = '';

  // Form data
  organizations: Organization[] = [];
  selectedOrg?: Organization;
  orgIncidentCategoryList: IncidentCategory[] = [];
  selectedIncidentCategory?: IncidentCategory;
  incidentTypeList: IncidentType[] = [
    { label: 'Public', type: 'public' },
    { label: 'Private', type: 'private' },
  ];
  selectedIncidentType?: IncidentType;
  isBuildingSpecificOrg = false;
  // Building data
  building: any;
  campus: any;
  floorList: Floor[] = [];
  selectedFloor?: Floor;
  roomList: Room[] = [];
  selectedRoom?: Room;
  campusList: any[] = [];
  selectedCampus?: any;
  buildingList: any[] = [];
  selectedBuilding?: any;

  // Form inputs
  incidentName = '';
  incidentDescription = '';

  // Permissions
  permissions?: Permission;

  private sub = new Subscription();

  @Input() dialogData: any;
  @Output() close = new EventEmitter<any>();
  @Output() opened = new EventEmitter<void>();
  showFormSection: boolean = false;
  isCampusIncident : boolean = false;
  campusBuildings : boolean = false;

  constructor(
    private pinService: PinActivationService,
    private orgUtil: OrganizationUtilService,
    private dataService: DataService,
    private globalSettings: GlobalSettingsService,
    private settingService: SettingsService,
    private incidentCategoryService: IncidentCategoryService,
    private modalService: NgbModal,
    private illumLiveService: IllumLiveService,
    private activeModal: NgbActiveModal,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dialogData'] && this.dialogData && this.dialogData.perimeterId) {
      this.getCampusList();
      this.initializeDialog(this.dialogData);
      this.loadOrgIncidentCategoryInfo();
      this.getActionSessionForCreatePerimeter();
      this.getFloorInfo();
    }
    this.getCampusState();
  }

  getCampusState() {
    this.isCampusIncident = this.orgUtil.getCampusState();
  }

  ngOnInit() {
    this.opened.emit();
    this.sub.add(
      this.pinService.organizations$.subscribe(() => this.setupOrganizations())
    );
    this.sub.add(
      this.pinService.incidentCategories$.subscribe(() => this.setupIncidentCategories())
    );
    this.sub.add(
      this.pinService.permissions$.subscribe((permissions) => {
        this.permissions = permissions || undefined;
        this.resetTypeMenu();
      })
    );
    this.showFormSection = false;
  }

  getCampusList() {
    const perimeterId = this.dialogData?.perimeterId;
    this.campus = _.find(
      this.orgUtil.data.myBuildings,
      (o: any) => o.perimeterId === perimeterId
    );

    let assbuildings: any = [
      {
        id: '-1',
        label: 'All',        
        number: -1,
        numberCamera: 0,
        campusId: this.campus ? this.campus.id : '',
      },
    ];
    let autoNumber = 1;
    if (this.campus && Array.isArray(this.campus.associatedBuilding)) {
      this.campus.associatedBuilding.forEach((assBuilding: any) => {
        assbuildings.push({
          id: assBuilding.perimeterId,
          label: assBuilding.name,
          number: assBuilding.number ?? autoNumber++,
          numberCamera: assBuilding.numberCamera || 0,
          campusId: this.campus.id,
        });
      });
    }
    this.buildingList = assbuildings;
    let selectedBuilding;
    this.selectedBuilding = selectedBuilding || this.buildingList[0];
  }

  loadOrgIncidentCategoryInfo() {
    const orgId = this.dialogData?.organizationId;
    // if (!orgId) return;
    this.orgUtil.getIncidentCategoryInfo(orgId).subscribe({
      next: (result) => {
        this.orgIncidentCategoryDict = result;
        this.getAllOrgsForIncident();
      },
      error: (err) => {
        console.error('Failed to load incident categories', err);
      },
    });
  }

  getAllOrgsForIncident() {
    const perimeterId = this.globalSettings.getPerimeterIdForIncidentCreation();

    if (perimeterId) {
      this.orgUtil.getOrgsAvailForIncident(perimeterId).subscribe({
        next: (orgIds: string[]) => {
          const originalOrgIdForIncidentCreation =
            this.globalSettings.getOriginalOrgIdForIncidentCreation();
          if (orgIds.length === 0) {
            orgIds = [originalOrgIdForIncidentCreation];
          }

          if (this.isCampusIncident && this.campus) {
            const campusOrgIds = new Set<string>();
          
            if (this.campus.associatedOrgs) {
              Object.keys(this.campus.associatedOrgs).forEach((key) => {
                campusOrgIds.add(key);
              });
            }
            if (this.campus.associatedBuilding) {
              this.campus.associatedBuilding.forEach((b: any) => {
                if (b.organizationId) campusOrgIds.add(b.organizationId);
                if(b.assignedOrgId) {
                  campusOrgIds.add(b.assignedOrgId);
                } else if (b.associatedOrgs) {
                  Object.keys(b.associatedOrgs).forEach((key) => {
                    campusOrgIds.add(key);
                  });
                }
              });
            }
            orgIds = Array.from(campusOrgIds);
          }

          // Find the top org (lowest id numerically)
          const topOrgId = _.orderBy(orgIds, (o) => parseInt(o))[0];

          // Map orgIds to org objects
          let orgs = orgIds.map((orgId) => this.orgUtil.myOrgs[orgId]);

          // Remove undefined
          orgs = orgs.filter((org) => !!org);

          // Sort orgs by label (asc)
          orgs = _.orderBy(orgs, ['name'], ['asc']);

          // Remove top org from the sorted list
          orgs = orgs.filter((o) => o.id !== topOrgId);

          // Add top org to the beginning 
          orgs = _.concat([this.orgUtil.myOrgs[topOrgId]], orgs);

          if (this.isCampusIncident) {
            const campusOrgs = new Set(orgs.map(o => o.id)); 
            const allOrg: any = { id: orgIds, name: 'All', orgData: {} };
            
            orgs = _.concat(allOrg, orgs);
          }

          this.organizations = orgs;
          
          if (this.isCampusIncident) {
            const allOrg = this.organizations.find(o => o.name === 'All');
            if (allOrg) {
              this.selectedOrg = allOrg;
            }
          } else if (this.organizations.length > 0 && !this.selectedOrg) {
            this.selectedOrg = this.organizations[0];
          }
          if (this.dialogData?.orgIncidentCategoryId) {
            // Flatten all categories and find the org for the OIC
            const allCategories = Object.values(
              this.orgIncidentCategoryDict
            ).flat();
            const orgOfOic = allCategories.find(
              (cat) => cat.id === this.dialogData.orgIncidentCategoryId
            );
            if (orgOfOic) {
              // this.selectedOrg = this.organizations.find(org => org.id === orgOfOic.organizationId);
            }
          } else {
            if (this.isCampusIncident) {
            const allOrg = this.organizations.find(o => o.name === 'All');
            if (allOrg) {
              this.selectedOrg = allOrg;
            }
            } else {
            this.selectedOrg =
              this.organizations.find(
                (org) => org.id === originalOrgIdForIncidentCreation
              ) || this.organizations[0];
            }
          }

          // Now populate OIC combo based on selected org
          this.onSelectedOrgChanged();

          // Update header message
          this.headerMsg =
            this.organizations.length >= 2
              ? 'To Activate incident, select organization and enter PIN. Once incident is active, users inside the geofence will be notified of this event.'
              : 'To Activate incident, enter PIN. Once incident is active, users inside the geofence will be notified of this event.';
        },
        error: (error) => {
        },
      });
    } else {
      // Ad hoc incident
      const incidentId = this.dialogData?.incidentId;
      if (incidentId) {
        // If you have a method to get orgId for live incident, call it here
        // const orgId = this.pinService.getOrgIdForLiveIncident(incidentId);
        // if (orgId) {
        //   this.pinService.setManagingOrgId(orgId);
        // }
      }
    }
  }

  getFloorInfo() {
    const perimeterId = this.dialogData?.perimeterId;
    this.building = _.find(
      this.orgUtil.data.myBuildings,
      (o: any) => o.perimeterId === perimeterId
    );

    let floors: Floor[] = [
      {
        id: '-1',
        label: 'All',
        rooms: [],
        number: -1,
        numberCamera: 0,
        buildingId: this.building ? this.building.id : '',
      },
    ];
    if (this.building && Array.isArray(this.building.floorPerimeters)) {
      this.building.floorPerimeters.forEach((floor: any) => {
        floors.push({
          id: floor.perimeterId,
          label: floor.name,
          rooms: floor.rooms || [],
          number: floor.number || 0,
          numberCamera: floor.numberCamera || 0,
          buildingId: this.building.id,
        });
      });
    }
    this.floorList = floors;

    let selectedFloor;
    if (this.dialogData?.selectedFloorPerimeterId) {
      selectedFloor = _.find(this.floorList, {
        id: this.dialogData.selectedFloorPerimeterId,
      });
    }
    this.selectedFloor = selectedFloor || this.floorList[0];

    this.floorSelected();
  }

  private initializeDialog(data: PinActivationDialogData) {
    this.isActivateIncident = data.type === 'ACTIVATE_INCIDENT';
    this.isOperateIncident = data.type === 'OPERATE_ACTIVE_INCIDENT';
    this.incidentCreatedAndIsPending = !!data.incidentId;

    if (this.isActivateIncident) {
      this.headerMsg =
        this.organizations.length >= 2
          ? 'To Activate incident, select organization and enter PIN. Once incident is active, users inside the geofence will be notified of this event.'
          : 'To Activate incident, enter PIN. Once incident is active, users inside the geofence will be notified of this event.';
      this.bottomMsg =
        'Click on <strong>Activate and Go</strong> to activate the incident, and then enter Live mode.';
    } else if (this.isOperateIncident) {
      this.headerMsg = 'To operate inside Live mode, you must enter your PIN.';
    }

    if (this.isActivateIncident && !this.incidentCreatedAndIsPending) {
      // this.loadInitialData();
    }
  }

  private setupOrganizations() {
    if (this.organizations.length > 0) {
      this.selectedOrg = this.organizations[0];
      this.onSelectedOrgChanged();
    }
  }

  private setupIncidentCategories() {
    if (this.orgIncidentCategoryList.length > 0) {
      this.selectedIncidentCategory = this.orgIncidentCategoryList[0];
      this.incidentCategorySelected();
    }
  }
  onSelectedOrgChanged() {
    let selOrgId = this.selectedOrg?.id ?? '';
    if (!selOrgId) return;

    if (Array.isArray(selOrgId)) {
      selOrgId = selOrgId[0] || '';
    }

    if (!selOrgId) return;
    let oics: IncidentCategory[] = this.orgIncidentCategoryDict[selOrgId] || [];

    // Add "Custom" option with all required fields
    oics = [
      ...oics,
      {
        id: null,
        label: 'Custom',
        public: false,
        iconCode: '',
        description: '',
      },
    ];

    let orgIds: string[] = [];
    if (this.permissions?.publicPerimeter?.rows) {
      this.permissions.publicPerimeter.rows.forEach((o: any) => {
        orgIds.push(...o.organizationIds);
      });
    }

    // If not public, set all to private
    const isPublic = orgIds.includes(selOrgId);
    if (!isPublic) {
      oics = oics.map((o) => ({ ...o, public: false }));
    }

    this.orgIncidentCategoryList = oics;
    this.selectedIncidentCategory = this.orgIncidentCategoryList[0];

    if (this.dialogData?.orgIncidentCategoryId) {
      const initialWasInList = this.orgIncidentCategoryList.find(
        (o) => o.id === this.dialogData.orgIncidentCategoryId
      );
      if (initialWasInList) {
        this.selectedIncidentCategory = initialWasInList;
      }
    } else {
      this.selectedIncidentCategory =
        this.orgIncidentCategoryList[this.orgIncidentCategoryList.length - 1];
    }

    this.resetTypeMenu();
    this.incidentCategorySelected();
  }

  incidentCategorySelected() {
    if (!this.selectedIncidentCategory) return;

    this.incidentName = this.selectedIncidentCategory.label;
    this.incidentDescription = this.selectedIncidentCategory.description;

    if (this.incidentTypeList.length === 1) {
      this.selectedIncidentType = this.incidentTypeList[0];
    } else if (this.selectedIncidentCategory.public) {
      this.selectedIncidentType = this.incidentTypeList[0]; // Public
    } else {
      this.selectedIncidentType = this.incidentTypeList[1]; // Private
    }
  }

  floorSelected() {
    // Initialize room list with "All"
    this.roomList = [
      {
        id: '-1',
        label: 'All',
        labelShort: '',
        numberCamera: 0,
        floorId: '',
        buildingId: '',
      },
    ];

    // Find the selected floor entity in the building
    const floorEntity = this.building?.floorPerimeters?.find(
      (floor: any) => floor.perimeterId === this.selectedFloor?.id
    );

    if (floorEntity && floorEntity.perimeters) {
      const perimetersArray = Array.isArray(floorEntity.perimeters)
        ? floorEntity.perimeters
        : Object.values(floorEntity.perimeters);

      perimetersArray.forEach((p: any) => {
        if (p.arcAngelId !== floorEntity.perimeterId) {
          this.roomList.push({
            id: p.arcAngelId,
            label: p.name,
            labelShort: '',
            numberCamera: 0,
            floorId: '',
            buildingId: '',
          });
        }
      });
    }
    let selectedRoom;
    if (this.dialogData?.selectedRoomPerimeterId) {
      selectedRoom = this.roomList.find(
        (room) => room.id === this.dialogData.selectedRoomPerimeterId
      );
    }

    this.selectedRoom = selectedRoom || this.roomList[0];
  }

  onBuildingChanged() {
    if (!this.isCampusIncident) return;

    if (this.selectedBuilding && this.selectedBuilding.id !== '-1') {
      const building = this.campus?.associatedBuilding?.find(
        (b: any) => b.perimeterId === this.selectedBuilding.id
      );

      if (building?.associatedOrgs) {
        const orgIds = Object.keys(building.associatedOrgs);

        if (orgIds.length > 0) {
          const buildingOrgId = orgIds[0]; // take first key

          const org = this.organizations.find(
            (o) => o.id === buildingOrgId
          );

          if (org) {
            this.selectedOrg = org;
            this.isBuildingSpecificOrg = true; // 🔥 lock other orgs
            this.onSelectedOrgChanged();
          }
        }
      }
    } else {
      // Building = All
      const allOrg = this.organizations.find((o) => o.name === 'All');

      if (allOrg) {
        this.selectedOrg = allOrg;
        this.isBuildingSpecificOrg = false; // enable all orgs
        this.onSelectedOrgChanged();
      }
    }
  }

  private resetTypeMenu() {
    if (!this.permissions || !this.selectedOrg) return;

    const privateAllowed = this.hasPrivatePermission();
    const publicAllowed = this.hasPublicPermission();

    this.incidentTypeList = [];
    if (publicAllowed) {
      this.incidentTypeList.push({ label: 'Public', type: 'public' });
    }
    if (privateAllowed) {
      this.incidentTypeList.push({ label: 'Private', type: 'private' });
    }
  }

  private hasPrivatePermission(): boolean {
    if (!this.permissions || !this.selectedOrg) return false;
    return true;
  }

  private hasPublicPermission(): boolean {
    if (!this.permissions || !this.selectedOrg) return false;
    return true;
  }

  activate() {
    this.settingService.verifyPin(this.pinValue).subscribe(
      (response) => {
        if (response.validPin) {
          this.closeDialog('ACTIVATE', false);
        } else {
          this.showAlert('Invalid PIN');
        }
      },
      () => {
        this.showAlert('Invalid PIN');
      }
    );
  }
  confirmPublicIncident() {
    // Permission check if creating
    if (this.isActivateIncident && !this.incidentCreatedAndIsPending) {
      let permission = false;
      if (this.selectedIncidentType?.type === 'public') {
        permission = !!this.permissions?.publicPerimeter?.rows?.length;
      }
      if (this.selectedIncidentType?.type === 'private') {
        permission = !!this.permissions?.privatePerimeter?.rows?.length;
      }

      if (!permission) {
        this.showAlert(
          'Not permitted to create ' +
          this.selectedIncidentType?.type +
          ' incident'
        );
        return;
      }
    }

    if (
      this.selectedIncidentType?.type === 'public' &&
      !this.incidentCreatedAndIsPending
    ) {
      this.showConfirm('You are creating a public incident. Are you sure?').then(confirmed => {
        if (confirmed) {
          this.activateAndGo();
        }
      });
    } else {
      this.activateAndGo();
    }
  }
  activateAndGo() {
    if (this.isActivateIncident && !this.incidentCreatedAndIsPending) {
      if (!this.incidentName.trim()) {
        this.showAlert('Missing name');
        return;
      }
      if (!this.incidentDescription.trim()) {
        this.showAlert('Missing description');
        return;
      }
      if (!this.selectedIncidentCategory) {
        this.showAlert('Missing category');
        return;
      }
    }

    this.settingService.verifyPin(this.pinValue).subscribe(
      (response) => {
        if (response.validPin) {
          this.showFormSection = true;
          if (this.selectedOrg) {
            const orgId = this.selectedOrg?.id ?? '';
            this.incidentCategoryService.setManagingOrgId?.(orgId);
            this.illumLiveService.setManagingOrgId?.(orgId);  
          }
          this.globalSettings.setPerimeterIdForIncidentCreation(null);

          let catId = this.selectedIncidentCategory?.id;
          let isPublic = false;
          if (this.isActivateIncident && !this.incidentCreatedAndIsPending) {
            if (
              catId &&
              parseInt(catId, 10) === this.orgIncidentCategoryList.length
            ) {
              catId = undefined;
            }
            isPublic = this.selectedIncidentType?.type === 'public';
          }

          let perimeterIds: any[] = [];
          // let perimeterId = this.dialogData?.perimeterId;
          // if (this.selectedRoom && this.selectedRoom.id !== '-1') {
          //   perimeterId = this.selectedRoom.id;
          // } else if (this.selectedFloor && this.selectedFloor.id !== '-1') {
          //   perimeterId = this.selectedFloor.id;
          // }
          // if (this.isCampusIncident){
          //   perimeterId = this.dialogData?.perimeterId;
          //   if(this.isCampusIncident){

          //   }
          // }

          if (this.isCampusIncident) {
            const campusPerimeterId = this.dialogData?.perimeterId;

            if (this.selectedBuilding?.id === '-1') {
              const buildingIds =
                this.campus?.associatedBuilding?.map(
                  (b: any) => b.perimeterId
                ) || [];

              perimeterIds = [campusPerimeterId, ...buildingIds];
              this.campusBuildings = true;
            } else {
              perimeterIds = [
                this.selectedBuilding?.id
              ];
              this.campusBuildings = false;
            }
          } else {
            let perimeterId = this.dialogData?.perimeterId;

            if (this.selectedRoom && this.selectedRoom.id !== '-1') {
              perimeterId = this.selectedRoom.id;
            } else if (this.selectedFloor && this.selectedFloor.id !== '-1') {
              perimeterId = this.selectedFloor.id;
            }

            perimeterIds = perimeterId;
          }
          const alertId = this.dialogData?.alertId;

          const incidentOptions = {
            alertId: alertId,
            orgIncidentCategoryId: catId,
            public: isPublic,
            label: this.incidentName,
            description: this.incidentDescription,
            perimeterId: Array.isArray(perimeterIds)
              ? perimeterIds
              : [perimeterIds],
            focalPoint: this.dialogData?.focalPoint,
          };

          this.globalSettings.setIncidentOptions?.(incidentOptions);
          if (this.campusBuildings) {
            this.closeDialog('CAMPUS_ACTIVATE_AND_GO');
          } else {
            this.closeDialog('ACTIVATE_AND_GO');
          }
        } else {
          this.showAlert('Invalid PIN');
        }
      },
      () => {
        this.showAlert('Invalid PIN');
      }
    );
  }
  cancel() {
    this.closeDialog('cancel');
  }

  private closeDialog(result: string, data?: any) {
    this.activeModal.close(result);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  getActionSessionForCreatePerimeter() {
    this.dataService.getActionSessionForCreatePerimeter().subscribe({
      next: (permissions) => {
        Object.values(permissions).forEach((perm: any) => {
          if (perm.rows && perm.length > 0) {
            const topOrgs = perm.rows[0].organizationIds;
            topOrgs.forEach((topOrg: string) => {
              // You need access to ArcAngelService.data.myOrgs here
              // const org = ArcAngelService.data.myOrgs[topOrg].descendants
              // this.pinService.getOrgById(topOrg); // Implement this in your service
              // if (org && org.descendants && org.descendants.length > 0) {
              //   perm.data.rows[0].organizationIds.push(...org.descendants);
              // }
            });
          }
        });
        this.permissions = permissions;
        this.pinService.setPermissions(permissions); // If you use a service to share state
        this.resetTypeMenu();
      },
      error: (err) => {
        console.error('Failed to load permissions', err);
      },
    });
  }

  buildAncestorLabel(org: Organization): string {
    if (!org) return '';
    if (
      !org.orgData.parentId ||
      org.orgData.parentId === '1' ||
      org.orgData.parentId === '7'
    ) {
      return org.name ?? '';
    }
    const parent = this.orgUtil.myOrgs[org.orgData.parentId];
    if (!parent) {
      return org.name ?? '';
    }
    return this.buildAncestorLabel(parent) + ' -> ' + org.name;
  }

  private showAlert(message: string) {
    const modalRef = this.modalService.open(ResponseModalComponent, {
      centered: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top'
    });
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.showCancel = false;
    return modalRef.result;
  }

  private showConfirm(message: string): Promise<boolean> {
    const modalRef = this.modalService.open(ResponseModalComponent, {
      centered: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top'
    });
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.showCancel = true;
    return modalRef.result;
  }

  private extractErrorMessage(error: any): string {
    try {
      // Try message paths in priority order
      const msg =
        error?.error?.error?.message ||
        error?.error?.message ||
        error?.message ||
        (typeof error === 'string' ? error : null);

      if (typeof msg === 'string' && msg.trim()) {
        return msg.trim();
      }

      // Fallback using error code
      if (error?.error?.code) {
        return `Error: ${error.error.code}`;
      }

    } catch (e) {
      console.error('Error extracting error message:', e);
    }

    return 'Failed to create incident. Please try again.';
  }
}